/**
 * Inventory "In stock" + "Ordered (not arrived)" helpers.
 * Use these to read/write the two quantities and for threshold logic (in stock only).
 * Also: display + low detection for Overview/Needs-attention (units vs fullness).
 */

export type InventoryItemQuantity = {
  in_stock_qty?: number | null;
  ordered_qty?: number | null;
  /** Legacy; prefer in_stock_qty */
  current_count?: number | null;
};

/** Fullness (bottle level) item: optional current/threshold for display and low detection. */
export type InventoryItemForDisplay = InventoryItemQuantity & {
  quantity_type?: string | null;
  threshold_count?: number | null;
  current_value?: number | null;
  threshold_value?: number | null;
};

/** In stock: physically present. (Fallback to current_count for backfill compatibility.) */
export function getInStockCount(item: InventoryItemQuantity): number {
  const v = item.in_stock_qty ?? item.current_count ?? 0;
  return Math.max(0, typeof v === "number" ? v : 0);
}

/** Ordered (not arrived): purchased but not yet delivered/received. */
export function getOrderedCount(item: InventoryItemQuantity): number {
  const v = item.ordered_qty ?? 0;
  return Math.max(0, typeof v === "number" ? v : 0);
}

/** Threshold comparison uses in stock only (MVP). Count-based items only. */
export function isBelowThreshold(
  item: { threshold_count?: number | null } & InventoryItemQuantity
): boolean {
  const threshold = item.threshold_count ?? null;
  if (threshold == null) return false;
  return getInStockCount(item) <= threshold;
}

/** Human-readable label for fullness value (0–1). Matches InventoryTab fullness options. */
export function getFullnessLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return "Unknown";
  const labels: Record<number, string> = {
    1: "Full",
    0.75: "3/4 Full",
    0.5: "Half Full",
    0.25: "1/4 Full",
    0: "Empty",
  };
  return labels[value as keyof typeof labels] ?? `${Math.round(Number(value) * 100)}%`;
}

/**
 * Single source of truth for Overview/Needs-attention: display string, is low, and low label.
 * - Units (count): display "In stock: X", low when in_stock <= threshold_count.
 * - Fullness (bottle level): display "Fullness: 3/4 Full" (or %), low only when current_fullness <= threshold_fullness.
 */
export function getInventoryDisplayAndStatus(item: InventoryItemForDisplay): {
  displayValue: string;
  isLow: boolean;
  lowLabel: string | null;
} {
  const qtyType = (item.quantity_type ?? "units").toLowerCase();
  const isFullness = qtyType === "fullness";

  if (isFullness) {
    const current = item.current_value != null ? Number(item.current_value) : 1;
    const threshold = item.threshold_value != null ? Number(item.threshold_value) : 0.25;
    const isLow = current <= threshold;
    const displayValue = `Fullness: ${getFullnessLabel(item.current_value)}`;
    const lowLabel = isLow
      ? `Low: ${getFullnessLabel(item.current_value)} left (at/below threshold ${getFullnessLabel(threshold)})`
      : null;
    return { displayValue, isLow, lowLabel };
  }

  const inStock = getInStockCount(item);
  const thresholdCount = item.threshold_count != null ? Number(item.threshold_count) : null;
  const isLow = thresholdCount != null && inStock <= thresholdCount;
  const displayValue = `In stock: ${inStock}`;
  const lowLabel = isLow ? `Low: ${inStock} (threshold ${thresholdCount})` : null;
  return { displayValue, isLow, lowLabel };
}
